import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Avatar,
  Tooltip,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Grid,
  TableSortLabel,
  Autocomplete,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  MoreVert,
  AdminPanelSettings,
  Person,
  Block,
  Visibility,
  Search,
  Star,
  StarBorder,
  Refresh,
  LockReset,
  VerifiedUser,
  DeleteForever,
  UploadFile,
  CheckCircle,
  ErrorOutline,
  Close,
  Campaign,
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxIcon,
  School,
  Undo
} from '@mui/icons-material';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');

  // Sort state
  const [sortCol, setSortCol] = useState('last_sign_in_at');
  const [sortDir, setSortDir] = useState('desc');

  const getAuthHeader = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeader();
      const response = await axios.get('/api/admin/users', {
        params: { page, perPage: rowsPerPage, search: searchTerm || undefined },
        headers,
      });
      setUsers(response.data.users || []);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError('Failed to fetch users: ' + msg);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [premiumDuration, setPremiumDuration] = useState('1 year');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadResults, setUploadResults] = useState([]);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastFile, setBroadcastFile] = useState(null);
  const [broadcastSelectedUsers, setBroadcastSelectedUsers] = useState([]);
  const [broadcastResults, setBroadcastResults] = useState([]);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [deckBroadcastOpen, setDeckBroadcastOpen] = useState(false);
  const [deckSourceUser, setDeckSourceUser] = useState(null);
  const [deckSourceDecks, setDeckSourceDecks] = useState([]);
  const [deckSourceDecksLoading, setDeckSourceDecksLoading] = useState(false);
  const [deckSelectedDeck, setDeckSelectedDeck] = useState(null);
  const [deckTargetUsers, setDeckTargetUsers] = useState([]);
  const [deckResults, setDeckResults] = useState([]);
  const [deckLoading, setDeckLoading] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallDeckName, setRecallDeckName] = useState('');
  const [recallLoading, setRecallLoading] = useState(false);
  const [recallResult, setRecallResult] = useState(null);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDialogOpen = (type) => {
    setDialogType(type);
    setDialogOpen(true);
    setDeleteConfirmEmail('');
    setPremiumDuration('1 year');
    handleMenuClose();
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogType('');
    setSelectedUser(null);
    setDeleteConfirmEmail('');
    setUploadFiles([]);
    setUploadResults([]);
    setMessage('');
    setError('');
  };

  const handleBroadcastClose = () => {
    setBroadcastOpen(false);
    setBroadcastFile(null);
    setBroadcastSelectedUsers([]);
    setBroadcastResults([]);
  };

  const handleDeckBroadcastClose = () => {
    setDeckBroadcastOpen(false);
    setDeckSourceUser(null);
    setDeckSourceDecks([]);
    setDeckSelectedDeck(null);
    setDeckTargetUsers([]);
    setDeckResults([]);
  };

  const fetchDecksForUser = async (userId) => {
    setDeckSourceDecksLoading(true);
    setDeckSourceDecks([]);
    setDeckSelectedDeck(null);
    try {
      const headers = await getAuthHeader();
      const response = await axios.get('/api/admin/users', {
        params: { listDecks: userId },
        headers,
      });
      setDeckSourceDecks(response.data.decks || []);
    } catch (err) {
      setError('Failed to load decks: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeckSourceDecksLoading(false);
    }
  };

  const handleUserAction = async (action, extra = {}) => {
    if (!selectedUser) return;
    setActionLoading(true);
    setMessage('');
    setError('');
    try {
      const headers = await getAuthHeader();
      const response = await axios.post('/api/admin/users', {
        action,
        userId: selectedUser.id,
        ...extra,
      }, { headers });

      setMessage(response.data.message || `Action '${action}' completed`);
      await fetchUsers();
      if (action !== 'send_password_reset' && action !== 'confirm_email') {
        handleDialogClose();
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setError('Action failed: ' + msg);
    } finally {
      setActionLoading(false);
    }
  };

  const getUserStatus = (user) => {
    if (user.banned_until) return { label: 'Suspended', color: 'error' };
    if (!user.email_confirmed_at) return { label: 'Unverified', color: 'warning' };
    if (user.app_metadata?.isAdmin || user.user_metadata?.isAdmin) return { label: 'Admin', color: 'primary' };
    return { label: 'Active', color: 'success' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getProvider = (user) => {
    const provider = user.app_metadata?.provider || user.app_metadata?.providers?.[0] || 'email';
    return provider.charAt(0).toUpperCase() + provider.slice(1);
  };

  const handleSort = (col) => {
    setSortDir(sortCol === col && sortDir === 'asc' ? 'desc' : 'asc');
    setSortCol(col);
  };

  // Derive unique providers for filter dropdown
  const providerOptions = useMemo(() => {
    const set = new Set(users.map((u) => getProvider(u)));
    return Array.from(set).sort();
  }, [users]);

  const filteredAndSortedUsers = useMemo(() => {
    const getStatusLabel = (user) => getUserStatus(user).label;
    let rows = [...users];
    if (statusFilter) rows = rows.filter((u) => getStatusLabel(u) === statusFilter);
    if (tierFilter) rows = rows.filter((u) => (u.subscription?.tier || 'free') === tierFilter);
    if (providerFilter) rows = rows.filter((u) => getProvider(u) === providerFilter);

    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case 'email': av = a.email || ''; bv = b.email || ''; break;
        case 'status': av = getUserStatus(a).label; bv = getUserStatus(b).label; break;
        case 'tier': av = a.subscription?.tier || 'free'; bv = b.subscription?.tier || 'free'; break;
        case 'provider': av = getProvider(a); bv = getProvider(b); break;
        case 'totalWords': av = a.stats?.totalWords || 0; bv = b.stats?.totalWords || 0; break;
        case 'flashcards': av = a.stats?.flashcards || 0; bv = b.stats?.flashcards || 0; break;
        case 'documents': av = a.stats?.documents || 0; bv = b.stats?.documents || 0; break;
        case 'created_at': av = a.created_at || ''; bv = b.created_at || ''; break;
        case 'last_sign_in_at': av = a.last_sign_in_at || ''; bv = b.last_sign_in_at || ''; break;
        default: av = ''; bv = '';
      }
      if (typeof av === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [users, statusFilter, tierFilter, providerFilter, sortCol, sortDir]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          User Management
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search /></InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<Campaign />}
            onClick={() => setBroadcastOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Broadcast File
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<School />}
            onClick={() => setDeckBroadcastOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Broadcast Deck
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            startIcon={<Undo />}
            onClick={() => { setRecallOpen(true); setRecallDeckName(''); setRecallResult(null); }}
            sx={{ textTransform: 'none' }}
          >
            Recall Broadcast
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchUsers} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filter Bar */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="Suspended">Suspended</MenuItem>
            <MenuItem value="Unverified">Unverified</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Tier</InputLabel>
          <Select value={tierFilter} label="Tier" onChange={(e) => setTierFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="free">Free</MenuItem>
            <MenuItem value="premium">Premium</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Provider</InputLabel>
          <Select value={providerFilter} label="Provider" onChange={(e) => setProviderFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {providerOptions.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {(statusFilter || tierFilter || providerFilter) && (
          <Button size="small" variant="outlined" onClick={() => { setStatusFilter(''); setTierFilter(''); setProviderFilter(''); }}>
            Clear Filters
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          {loading && users.length === 0 ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortCol === 'email' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'email'} direction={sortCol === 'email' ? sortDir : 'asc'} onClick={() => handleSort('email')}>User</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'status' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'status'} direction={sortCol === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'tier' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'tier'} direction={sortCol === 'tier' ? sortDir : 'asc'} onClick={() => handleSort('tier')}>Tier</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'provider' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'provider'} direction={sortCol === 'provider' ? sortDir : 'asc'} onClick={() => handleSort('provider')}>Provider</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortCol === 'totalWords' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'totalWords'} direction={sortCol === 'totalWords' ? sortDir : 'asc'} onClick={() => handleSort('totalWords')}>Words</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortCol === 'flashcards' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'flashcards'} direction={sortCol === 'flashcards' ? sortDir : 'asc'} onClick={() => handleSort('flashcards')}>Flashcards</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortCol === 'documents' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'documents'} direction={sortCol === 'documents' ? sortDir : 'asc'} onClick={() => handleSort('documents')}>Docs</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'created_at' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'created_at'} direction={sortCol === 'created_at' ? sortDir : 'asc'} onClick={() => handleSort('created_at')}>Created</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'last_sign_in_at' ? sortDir : false}>
                    <TableSortLabel active={sortCol === 'last_sign_in_at'} direction={sortCol === 'last_sign_in_at' ? sortDir : 'asc'} onClick={() => handleSort('last_sign_in_at')}>Last Login</TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedUsers
                  .map((user) => {
                    const status = getUserStatus(user);
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                              {user.email.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {user.email}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                ID: {user.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={status.label}
                            color={status.color}
                            size="small"
                            icon={
                              status.label === 'Admin' ? <AdminPanelSettings /> : 
                              status.label === 'Suspended' ? <Block /> : <Person />
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.subscription?.tier === 'premium' ? 'Premium' : 'Free'}
                            color={user.subscription?.tier === 'premium' ? 'secondary' : 'default'}
                            size="small"
                            icon={user.subscription?.tier === 'premium' ? <Star /> : <StarBorder />}
                            variant={user.subscription?.tier === 'premium' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={getProvider(user)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          {(user.stats?.totalWords || 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {user.stats?.flashcards || 0}
                        </TableCell>
                        <TableCell align="right">
                          {user.stats?.documents || 0}
                        </TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        <TableCell>{formatDate(user.last_sign_in_at)}</TableCell>
                        <TableCell>
                          <Tooltip title="More actions">
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, user)}
                              size="small"
                            >
                              <MoreVert />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          )}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredAndSortedUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => { setAnchorEl(null); setSelectedUser(null); }}
      >
        <MenuItem onClick={() => handleDialogOpen('view')}>
          <Visibility sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <Divider />
        {/* Admin role toggle — hide "Remove Admin" when viewing yourself */}
        {(selectedUser?.app_metadata?.isAdmin || selectedUser?.user_metadata?.isAdmin) ? (
          selectedUser?.id !== currentUser?.id && (
            <MenuItem onClick={() => handleUserAction('demote')} disabled={actionLoading}>
              <Person sx={{ mr: 1 }} /> Remove Admin
            </MenuItem>
          )
        ) : (
          <MenuItem onClick={() => handleUserAction('promote')} disabled={actionLoading}>
            <AdminPanelSettings sx={{ mr: 1 }} /> Make Admin
          </MenuItem>
        )}
        {/* Tier toggle — hide for admin users (they are always premium) */}
        {!(selectedUser?.app_metadata?.isAdmin || selectedUser?.user_metadata?.isAdmin) && (
          selectedUser?.subscription?.tier === 'premium' ? (
            <MenuItem onClick={() => handleUserAction('downgrade_free')} disabled={actionLoading}>
              <StarBorder sx={{ mr: 1 }} /> Downgrade to Free
            </MenuItem>
          ) : (
            <MenuItem onClick={() => handleDialogOpen('premium_duration')}>
              <Star sx={{ mr: 1 }} /> Upgrade to Premium
            </MenuItem>
          )
        )}
        {/* Suspend/Reactivate — hide for admin users */}
        {!(selectedUser?.app_metadata?.isAdmin || selectedUser?.user_metadata?.isAdmin) && (
          selectedUser?.banned_until ? (
            <MenuItem onClick={() => handleUserAction('reactivate')} disabled={actionLoading}>
              <Person sx={{ mr: 1 }} /> Reactivate
            </MenuItem>
          ) : (
            <MenuItem onClick={() => handleUserAction('suspend')} disabled={actionLoading}>
              <Block sx={{ mr: 1 }} /> Suspend User
            </MenuItem>
          )
        )}
        <Divider />
        <MenuItem onClick={() => handleUserAction('send_password_reset')} disabled={actionLoading}>
          <LockReset sx={{ mr: 1 }} /> Send Password Reset
        </MenuItem>
        {!selectedUser?.email_confirmed_at && (
          <MenuItem onClick={() => handleUserAction('confirm_email')} disabled={actionLoading}>
            <VerifiedUser sx={{ mr: 1 }} /> Confirm Email
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => handleDialogOpen('upload_file')}>
          <UploadFile sx={{ mr: 1 }} /> Upload Files to Library
        </MenuItem>
        {/* Delete — hide for admin users */}
        {!(selectedUser?.app_metadata?.isAdmin || selectedUser?.user_metadata?.isAdmin) && (
          <>
            <Divider />
            <MenuItem onClick={() => handleDialogOpen('delete')} sx={{ color: 'error.main' }}>
              <DeleteForever sx={{ mr: 1 }} /> Delete User
            </MenuItem>
          </>
        )}
      </Menu>

      {/* View Details Dialog */}
      <Dialog open={dialogOpen && dialogType === 'view'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
              <Typography variant="body2">
                <strong>Email:</strong> {selectedUser.email}
              </Typography>
              <Typography variant="body2">
                <strong>User ID:</strong> <code style={{ fontSize: '0.8em' }}>{selectedUser.id}</code>
              </Typography>
              <Typography variant="body2">
                <strong>Provider:</strong> {getProvider(selectedUser)}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(selectedUser.created_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Last Login:</strong> {formatDate(selectedUser.last_sign_in_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Email Verified:</strong> {selectedUser.email_confirmed_at ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2">
                <strong>Role:</strong> {(selectedUser.app_metadata?.isAdmin || selectedUser.user_metadata?.isAdmin) ? 'Admin' : 'User'}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="textSecondary">Subscription</Typography>
              <Typography variant="body2">
                <strong>Tier:</strong> {selectedUser.subscription?.tier === 'premium' ? 'Premium' : 'Free'}
                {selectedUser.subscription?.premium_until && (
                  <> (until {formatDate(selectedUser.subscription.premium_until)})</>
                )}
              </Typography>
              {selectedUser.subscription?.stripe_status && (
                <Typography variant="body2">
                  <strong>Stripe Status:</strong> {selectedUser.subscription.stripe_status}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="textSecondary">Usage Statistics</Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Total Words</Typography>
                  <Typography variant="h6">{(selectedUser.stats?.totalWords || 0).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Flashcards</Typography>
                  <Typography variant="h6">{selectedUser.stats?.flashcards || 0}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="textSecondary">Documents</Typography>
                  <Typography variant="h6">{selectedUser.stats?.documents || 0}</Typography>
                </Grid>
              </Grid>
              {selectedUser.banned_until && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="error">
                    <strong>Suspended until:</strong> {formatDate(selectedUser.banned_until)}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Premium Duration Dialog */}
      <Dialog open={dialogOpen && dialogType === 'premium_duration'} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Upgrade to Premium</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pt: 1 }}>
            Set premium duration for <strong>{selectedUser?.email}</strong>
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Duration</InputLabel>
            <Select
              value={premiumDuration}
              label="Duration"
              onChange={(e) => setPremiumDuration(e.target.value)}
            >
              <MenuItem value="1 month">1 Month</MenuItem>
              <MenuItem value="6 months">6 Months</MenuItem>
              <MenuItem value="1 year">1 Year</MenuItem>
              <MenuItem value="forever">Forever</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={() => handleUserAction('upgrade_premium', { duration: premiumDuration })}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Upgrade'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={dialogOpen && dialogType === 'delete'} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Delete User Permanently</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            This will permanently delete the user and all their data. This action cannot be undone.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Type <strong>{selectedUser?.email}</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            placeholder="Type email to confirm"
            value={deleteConfirmEmail}
            onChange={(e) => setDeleteConfirmEmail(e.target.value)}
            error={deleteConfirmEmail.length > 0 && deleteConfirmEmail !== selectedUser?.email}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            onClick={() => handleUserAction('delete_user')}
            variant="contained"
            color="error"
            disabled={actionLoading || deleteConfirmEmail !== selectedUser?.email}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Upload Files Dialog */}
      <Dialog open={dialogOpen && dialogType === 'upload_file'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Upload Files to Library
          <IconButton onClick={handleDialogClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pt: 1 }}>
            Upload files to <strong>{selectedUser?.email}</strong>'s library. Max 25 MB per file.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
            fullWidth
            sx={{ mb: 2, py: 1.5, borderStyle: 'dashed' }}
          >
            Select Files (.pdf, .txt, .md)
            <input
              type="file"
              hidden
              multiple
              accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
              onChange={(e) => {
                const selected = Array.from(e.target.files || []);
                setUploadFiles((prev) => [...prev, ...selected]);
                e.target.value = '';
              }}
            />
          </Button>
          {uploadFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {uploadFiles.length} file(s) selected
              </Typography>
              {uploadFiles.map((f, i) => (
                <Box key={i} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover', mb: 0.5 }}>
                  <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                    {f.name} ({(f.size / 1024).toFixed(1)} KB)
                  </Typography>
                  <IconButton size="small" onClick={() => setUploadFiles((prev) => prev.filter((_, idx) => idx !== i))}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
          {uploadResults.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Results</Typography>
              {uploadResults.map((r, i) => (
                <Box key={i} display="flex" alignItems="center" gap={1} sx={{ py: 0.5 }}>
                  {r.success
                    ? <CheckCircle fontSize="small" color="success" />
                    : <ErrorOutline fontSize="small" color="error" />}
                  <Typography variant="body2" color={r.success ? 'success.main' : 'error.main'}>
                    {r.fileName}{r.error ? ` — ${r.error}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { handleDialogClose(); setUploadFiles([]); setUploadResults([]); }}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!selectedUser || uploadFiles.length === 0) return;
              setActionLoading(true);
              setUploadResults([]);
              setError('');
              try {
                const filePayloads = await Promise.all(
                  uploadFiles.map((f) => new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                      const base64 = reader.result.split(',')[1];
                      resolve({ fileName: f.name, fileType: f.type, fileSize: f.size, base64Content: base64 });
                    };
                    reader.onerror = () => reject(new Error(`Failed to read ${f.name}`));
                    reader.readAsDataURL(f);
                  }))
                );
                const headers = await getAuthHeader();
                const response = await axios.post('/api/admin/users', {
                  action: 'upload_to_user_library',
                  userId: selectedUser.id,
                  files: filePayloads,
                }, { headers });
                setUploadResults(response.data.results || []);
                setMessage(response.data.message || 'Upload complete');
                setUploadFiles([]);
                await fetchUsers();
              } catch (err) {
                const msg = err.response?.data?.error || err.message;
                setError('Upload failed: ' + msg);
              } finally {
                setActionLoading(false);
              }
            }}
            variant="contained"
            disabled={actionLoading || uploadFiles.length === 0}
            startIcon={actionLoading ? <CircularProgress size={18} /> : <UploadFile />}
          >
            {actionLoading ? 'Uploading...' : `Upload ${uploadFiles.length} File(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast File Dialog */}
      <Dialog open={broadcastOpen} onClose={handleBroadcastClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Broadcast File to Users
          <IconButton onClick={handleBroadcastClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pt: 1 }}>
            Upload a file once and send it to multiple users' libraries. The file is stored only once to save space.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
            fullWidth
            sx={{ mb: 2, py: 1.5, borderStyle: 'dashed' }}
          >
            {broadcastFile ? broadcastFile.name : 'Select File (.pdf, .txt, .md)'}
            <input
              type="file"
              hidden
              accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setBroadcastFile(f);
                e.target.value = '';
              }}
            />
          </Button>
          {broadcastFile && (
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2, py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
              <Typography variant="body2" noWrap sx={{ flex: 1, mr: 1 }}>
                {broadcastFile.name} ({(broadcastFile.size / 1024).toFixed(1)} KB)
              </Typography>
              <IconButton size="small" onClick={() => setBroadcastFile(null)}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Select Users</Typography>
            <Button
              size="small"
              onClick={() => setBroadcastSelectedUsers(users.map((u) => u.id))}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Select All ({users.length})
            </Button>
            {broadcastSelectedUsers.length > 0 && (
              <Button
                size="small"
                onClick={() => setBroadcastSelectedUsers([])}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Clear
              </Button>
            )}
          </Box>
          <Autocomplete
            multiple
            options={users}
            disableCloseOnSelect
            getOptionLabel={(option) => option.email || option.id}
            value={users.filter((u) => broadcastSelectedUsers.includes(u.id))}
            onChange={(_, newVal) => setBroadcastSelectedUsers(newVal.map((u) => u.id))}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  sx={{ mr: 1 }}
                />
                <ListItemText
                  primary={option.email}
                  secondary={option.subscription?.tier === 'premium' ? 'Premium' : 'Free'}
                />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Search users by email..."
              />
            )}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="textSecondary">
            {broadcastSelectedUsers.length} user(s) selected
          </Typography>
          {broadcastResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Results</Typography>
              {broadcastResults.map((r, i) => {
                const userEmail = users.find((u) => u.id === r.userId)?.email || r.userId;
                return (
                  <Box key={i} display="flex" alignItems="center" gap={1} sx={{ py: 0.3 }}>
                    {r.success
                      ? <CheckCircle fontSize="small" color="success" />
                      : <ErrorOutline fontSize="small" color="error" />}
                    <Typography variant="body2" color={r.success ? 'success.main' : 'error.main'}>
                      {userEmail}{r.error ? ` — ${r.error}` : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBroadcastClose}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!broadcastFile || broadcastSelectedUsers.length === 0) return;
              setBroadcastLoading(true);
              setBroadcastResults([]);
              setError('');
              try {
                const base64 = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onload = () => resolve(reader.result.split(',')[1]);
                  reader.onerror = () => reject(new Error('Failed to read file'));
                  reader.readAsDataURL(broadcastFile);
                });
                const headers = await getAuthHeader();
                const response = await axios.post('/api/admin/users', {
                  action: 'broadcast_file_to_users',
                  userId: '_broadcast',
                  fileName: broadcastFile.name,
                  fileType: broadcastFile.type,
                  fileSize: broadcastFile.size,
                  base64Content: base64,
                  userIds: broadcastSelectedUsers,
                }, { headers });
                setBroadcastResults(response.data.results || []);
                setMessage(response.data.message || 'Broadcast complete');
                setBroadcastFile(null);
                setBroadcastSelectedUsers([]);
                await fetchUsers();
              } catch (err) {
                const msg = err.response?.data?.error || err.message;
                setError('Broadcast failed: ' + msg);
              } finally {
                setBroadcastLoading(false);
              }
            }}
            variant="contained"
            disabled={broadcastLoading || !broadcastFile || broadcastSelectedUsers.length === 0}
            startIcon={broadcastLoading ? <CircularProgress size={18} /> : <Campaign />}
          >
            {broadcastLoading ? 'Sending...' : `Send to ${broadcastSelectedUsers.length} User(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Deck Dialog */}
      <Dialog open={deckBroadcastOpen} onClose={handleDeckBroadcastClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Broadcast Flashcard Deck
          <IconButton onClick={handleDeckBroadcastClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pt: 1 }}>
            Copy a flashcard deck (with all its cards) from one user to multiple target users.
          </Typography>

          {/* Step 1: Pick source user */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>1. Select source user</Typography>
          <Autocomplete
            options={users}
            getOptionLabel={(option) => option.email || option.id}
            value={deckSourceUser}
            onChange={(_, newVal) => {
              setDeckSourceUser(newVal);
              setDeckSourceDecks([]);
              setDeckSelectedDeck(null);
              if (newVal) fetchDecksForUser(newVal.id);
            }}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Search user by email..." />
            )}
            sx={{ mb: 2 }}
          />

          {/* Step 2: Pick deck */}
          {deckSourceUser && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>2. Select deck</Typography>
              {deckSourceDecksLoading ? (
                <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
              ) : deckSourceDecks.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>No decks found for this user.</Typography>
              ) : (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Deck</InputLabel>
                  <Select
                    value={deckSelectedDeck?.id || ''}
                    label="Deck"
                    onChange={(e) => {
                      const deck = deckSourceDecks.find((d) => d.id === e.target.value);
                      setDeckSelectedDeck(deck || null);
                    }}
                  >
                    {deckSourceDecks.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name} ({d.cardCount} cards)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          {/* Step 3: Pick target users */}
          {deckSelectedDeck && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>3. Select target users</Typography>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                <Button
                  size="small"
                  onClick={() => setDeckTargetUsers(users.filter((u) => u.id !== deckSourceUser?.id).map((u) => u.id))}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Select All ({users.filter((u) => u.id !== deckSourceUser?.id).length})
                </Button>
                {deckTargetUsers.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => setDeckTargetUsers([])}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
              <Autocomplete
                multiple
                options={users.filter((u) => u.id !== deckSourceUser?.id)}
                disableCloseOnSelect
                getOptionLabel={(option) => option.email || option.id}
                value={users.filter((u) => deckTargetUsers.includes(u.id))}
                onChange={(_, newVal) => setDeckTargetUsers(newVal.map((u) => u.id))}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.id}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    <ListItemText
                      primary={option.email}
                      secondary={option.subscription?.tier === 'premium' ? 'Premium' : 'Free'}
                    />
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} size="small" placeholder="Search users by email..." />
                )}
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="textSecondary">
                {deckTargetUsers.length} user(s) selected
              </Typography>
            </>
          )}

          {/* Results */}
          {deckResults.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Results</Typography>
              {deckResults.map((r, i) => {
                const userEmail = users.find((u) => u.id === r.userId)?.email || r.userId;
                return (
                  <Box key={i} display="flex" alignItems="center" gap={1} sx={{ py: 0.3 }}>
                    {r.success
                      ? <CheckCircle fontSize="small" color="success" />
                      : <ErrorOutline fontSize="small" color="error" />}
                    <Typography variant="body2" color={r.success ? 'success.main' : 'error.main'}>
                      {userEmail}{r.success && r.cardsCopied ? ` (${r.cardsCopied} cards)` : ''}{r.error ? ` — ${r.error}` : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={handleDeckBroadcastClose}>Cancel</Button>
          {deckSelectedDeck && (
            <Button
              onClick={async () => {
                setDeckLoading(true);
                setError('');
                try {
                  const headers = await getAuthHeader();
                  const response = await axios.post('/api/admin/users', {
                    action: 'send_deck_notifications',
                    userId: '_notify',
                    deckName: deckSelectedDeck.name,
                    cardCount: deckSelectedDeck.cardCount || 0,
                  }, { headers });
                  setMessage(response.data.message || 'Notifications sent');
                } catch (err) {
                  const msg = err.response?.data?.error || err.message;
                  setError('Notification failed: ' + msg);
                } finally {
                  setDeckLoading(false);
                }
              }}
              variant="outlined"
              color="warning"
              disabled={deckLoading}
              sx={{ textTransform: 'none' }}
            >
              🎁 Notify All Owners
            </Button>
          )}
          <Button
            onClick={async () => {
              if (!deckSelectedDeck || deckTargetUsers.length === 0) return;
              setDeckLoading(true);
              setDeckResults([]);
              setError('');
              try {
                const headers = await getAuthHeader();
                const response = await axios.post('/api/admin/users', {
                  action: 'broadcast_deck_to_users',
                  userId: '_broadcast',
                  sourceDeckId: deckSelectedDeck.id,
                  userIds: deckTargetUsers,
                }, { headers });
                setDeckResults(response.data.results || []);
                setMessage(response.data.message || 'Deck broadcast complete');
              } catch (err) {
                const msg = err.response?.data?.error || err.message;
                setError('Deck broadcast failed: ' + msg);
              } finally {
                setDeckLoading(false);
              }
            }}
            variant="contained"
            disabled={deckLoading || !deckSelectedDeck || deckTargetUsers.length === 0}
            startIcon={deckLoading ? <CircularProgress size={18} /> : <School />}
          >
            {deckLoading ? 'Sending...' : `Send to ${deckTargetUsers.length} User(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recall Broadcast Dialog */}
      <Dialog open={recallOpen} onClose={() => setRecallOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Recall Deck Broadcast
          <IconButton onClick={() => setRecallOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, pt: 1 }}>
            This will delete a deck (by exact name) from <strong>all users</strong> who have it, and dismiss any pending gift notifications for it.
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Deck name (exact match)"
            value={recallDeckName}
            onChange={(e) => setRecallDeckName(e.target.value)}
            placeholder="e.g. Welcome Deck"
            sx={{ mb: 1 }}
          />
          {recallResult && (
            <Alert severity={recallResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
              {recallResult.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecallOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={recallLoading || !recallDeckName.trim()}
            startIcon={recallLoading ? <CircularProgress size={18} /> : <Undo />}
            onClick={async () => {
              setRecallLoading(true);
              setRecallResult(null);
              try {
                const headers = await getAuthHeader();
                const response = await axios.post('/api/admin/users', {
                  action: 'recall_deck_broadcast',
                  userId: '_recall',
                  deckName: recallDeckName.trim(),
                }, { headers });
                setRecallResult({ success: true, message: response.data.message });
                setMessage(response.data.message);
              } catch (err) {
                const msg = err.response?.data?.error || err.message;
                setRecallResult({ success: false, message: msg });
              } finally {
                setRecallLoading(false);
              }
            }}
          >
            {recallLoading ? 'Recalling...' : 'Recall Broadcast'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
